/**
 * Authentication Context
 * Global auth state management
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import { tokenStorage } from '../utils/tokenStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();

    // Listen for storage changes (logout from another tab/device)
    const handleStorageChange = (e) => {
      // If tokens are cleared in another tab, logout this tab too
      if (e.key === 'accessToken' && !e.newValue) {
        console.log('Token removed in another tab, logging out...');
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const checkAuth = async () => {
    try {
      if (tokenStorage.hasToken()) {
        const response = await authService.getCurrentUser();
        setUser(response.user);
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      tokenStorage.clearTokens();
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (identifierOrUser, otpOrToken, typeOrRefreshToken = null) => {
    try {
      setError(null);

      // If typeOrRefreshToken is a string and looks like a JWT, this is a direct login (HR/Admin)
      if (typeOrRefreshToken && typeof typeOrRefreshToken === 'string' && typeOrRefreshToken.length > 20) {
        // Direct login with user data and tokens
        tokenStorage.setTokens(otpOrToken, typeOrRefreshToken);
        setUser(identifierOrUser);
        setIsAuthenticated(true);
        return { success: true, user: identifierOrUser };
      } else {
        // OTP verification login (Candidates)
        // typeOrRefreshToken can be 'email' or 'phone' to indicate the identifier type
        const isEmail = typeOrRefreshToken === 'email' || identifierOrUser.includes('@');
        
        let response;
        if (isEmail) {
          response = await authService.verifyEmailOTP(identifierOrUser, otpOrToken);
        } else {
          response = await authService.verifyOTP(identifierOrUser, otpOrToken);
        }

        if (response.success) {
          tokenStorage.setTokens(response.token, response.refreshToken);
          // Immediately hydrate full user profile (name/candidateId/etc.) so UI
          // does not briefly show fallback email until a manual refresh.
          let resolvedUser = response.user;
          try {
            const me = await authService.getCurrentUser();
            if (me?.user) {
              resolvedUser = me.user;
            }
          } catch (hydrateError) {
            // Keep OTP payload as fallback if /auth/me fails temporarily.
            console.warn('Failed to hydrate full user profile after OTP login:', hydrateError);
          }

          setUser(resolvedUser);
          setIsAuthenticated(true);
          return { success: true, user: resolvedUser };
        } else {
          setError(response.error);
          return { success: false, error: response.error };
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      tokenStorage.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await authService.getCurrentUser();
      setUser(response.user);
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshUserData,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
